/**
 * Supabase Auth Plugin
 * Works with @nuxtjs/supabase module
 * - Module handles client initialization and session management
 * - This plugin syncs user data and permissions
 * - Initializes Pinia auth store for centralized state management
 */
export default defineNuxtPlugin(async () => {
  const supabase = useSupabaseClient() // Auto-imported by module
  const route = useRoute()

  // Initialize Pinia auth store (client-side only)
  let authStore: ReturnType<typeof useAuthStore> | null = null
  if (import.meta.client) {
    authStore = useAuthStore()
  }

  // Initialize auth state from Supabase session
  if (import.meta.client) {
    // Skip initialization if we're on the callback page - it handles its own flow
    const isCallbackPage = route.path === '/auth/callback' || window.location.pathname === '/auth/callback'

    if (!isCallbackPage && authStore)
      await authStore.initialize()

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Skip if we're on the callback page - it handles its own flow
      const currentPath = window.location.pathname
      if (currentPath === '/auth/callback')
        return

      // Only sync on actual sign-in, not on token refresh or initial session
      if (event === 'SIGNED_IN' && session?.user) {
        // Update Pinia store (syncs user data internally)
        authStore?.setUser(session.user)
      }
      else if (event === 'SIGNED_OUT') {
        // Clear Pinia store state
        authStore?.setUser(null)
      }
      else if (event === 'TOKEN_REFRESHED' && session?.user) {
        // Silently update the user object so reactive tokens stay fresh
        if (authStore) {
          authStore.user = session.user
        }
      }
    })

    // Periodically verify the session is still valid (catches revoked refresh tokens).
    // The SDK's auto-refresh can silently fail, leaving stale state in the store.
    const SESSION_CHECK_INTERVAL = 5 * 60 * 1000 // 5 minutes

    const sessionCheckTimer = setInterval(async () => {
      try {
        const { data: { session: currentSession }, error } = await supabase.auth.getSession()

        // If we think we're logged in but there's no valid session, force logout
        if (authStore?.isAuthenticated && (!currentSession || error)) {
          console.warn('[Supabase Plugin] Session lost — clearing auth state and redirecting to login')
          authStore.setUser(null)
          await navigateTo('/login')
        }
      }
      catch {
        // getSession itself can throw on network errors — ignore silently
      }
    }, SESSION_CHECK_INTERVAL)

    // Cleanup on page unload (HMR / SPA teardown)
    window.addEventListener('beforeunload', () => {
      clearInterval(sessionCheckTimer)
      subscription.unsubscribe()
    })
  }
})
